import { useEffect, useRef } from "react";
import { get } from "lodash";
import { useQueryClient } from "react-query";
import { useStoreState } from "pullstate";
import { useAuth } from "@qure/qure-auth";
import ProxyAccountStore, { clearProxyTarget, confirmProxyTarget, notifyProxyRevoked, readStoredProxyTarget, stopProxyingLocally } from "../../utils/proxyAccountStore";
import { getProxyAccounts } from "../../types/api/ProxyAccount";
import useProxyAccess, { PROXY_ACCESS_QUERY_KEY } from "./useProxyAccess";
import constants from "../../utils/constants";

/**
 * Keeps the proxy selection correct: restores it after a reload, drops it if the
 * profile says it's no longer valid, and mirrors it across tabs. Call once, from
 * ProxyAccountSessionManager.
 */
var useProxyAccountSession = function useProxyAccountSession() {
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo,
    isLoading = _useAuth.isLoading,
    error = _useAuth.error;
  var _useProxyAccess = useProxyAccess(),
    access = _useProxyAccess.data,
    isAccessLoading = _useProxyAccess.isLoading;
  var queryClient = useQueryClient();
  var isLoggedIn = get(userInfo, ["authenticated"], false);
  var userEmail = get(userInfo, ["details", "email"], "");
  var proxyAccounts = getProxyAccounts(access);
  var targetAccountId = useStoreState(ProxyAccountStore, function (store) {
    return store.targetAccountId;
  });
  // Joined into a string so the effects below only re-run when the ids actually change.
  var proxyableAccountIdsKey = proxyAccounts.map(function (account) {
    return account.id;
  }).join(",");
  var proxyRejectionHandled = useRef(false);
  // Who's signed in right now. Changes when a profile call brings in a different user.
  var signedInUserKey = "".concat(isLoggedIn, "|").concat(userEmail);
  var prevSignedInUserKey = useRef(null);

  // Refetch /access whenever the signed-in identity changes (login, logout, switch),
  // so it never lags the profile. The first profile is just recorded, not acted on -
  // useProxyAccess already fetched on mount.
  useEffect(function () {
    if (isLoading) return;
    if (prevSignedInUserKey.current === null) {
      prevSignedInUserKey.current = signedInUserKey;
      return;
    }
    if (prevSignedInUserKey.current === signedInUserKey) return;
    prevSignedInUserKey.current = signedInUserKey;
    queryClient.invalidateQueries([PROXY_ACCESS_QUERY_KEY]);
  }, [isLoading, signedInUserKey]);

  // Handles a dead proxy target on the profile call. axiosConnect's 403 handler can't
  // catch it because the profile runs on qure-auth's own axios - without this the app
  // reads the 403 as logged-out and the target survives the reload, locking the user
  // out. Declared before the reconcile effect so it runs first.
  useEffect(function () {
    var isProxyRejection = get(error, ["response", "status"]) === 403 && get(error, ["response", "data", "proxied"]) === false;
    if (!isProxyRejection) {
      proxyRejectionHandled.current = false;
      return;
    }
    if (proxyRejectionHandled.current) return;
    proxyRejectionHandled.current = true;
    // Bumps revocationToken; ProxyAccountSessionManager toasts and refetches.
    notifyProxyRevoked();
  }, [error]);

  // Reconcile the intended target against what the server still grants. Runs on
  // boot and on every profile change.
  useEffect(function () {
    if (isLoading) return;
    if (!isLoggedIn || !userEmail) {
      // Logged out: never leave a target behind for the next user.
      if (targetAccountId) clearProxyTarget();
      return;
    }

    // Wait for the access list before reconciling, else an in-flight fetch reads
    // as an empty grant list and clears a still-valid target.
    if (isAccessLoading) return;
    var stored = readStoredProxyTarget();
    var restorableId = stored && stored.proxierEmail === userEmail ? stored.proxiedAccountId : null;
    var desiredId = targetAccountId !== null && targetAccountId !== void 0 ? targetAccountId : restorableId;
    if (!desiredId) return;
    var isStillGranted = proxyAccounts.some(function (account) {
      return account.id === desiredId;
    });
    if (isStillGranted) {
      confirmProxyTarget(desiredId);
    } else {
      clearProxyTarget();
    }
  }, [isLoading, isAccessLoading, isLoggedIn, userEmail, proxyableAccountIdsKey, targetAccountId]);

  // Mirror selections made in other tabs. The storage event only fires in tabs
  // other than the one that wrote, so this cannot loop. When the target actually
  // changes, reload like ProxyAccountSwitcher does: swapping only the header would
  // leave this tab showing the previous identity's profile and cached queries.
  useEffect(function () {
    var onStorage = function onStorage(event) {
      // A null key means localStorage was cleared wholesale - treat as removal.
      if (event.key !== null && event.key !== constants.PERSISTED.proxyTargetAccount) {
        return;
      }
      var currentId = ProxyAccountStore.getRawState().targetAccountId;
      var stored = readStoredProxyTarget();
      if (!stored) {
        stopProxyingLocally();
        if (currentId !== null) window.location.reload();
        return;
      }
      if (stored.proxierEmail !== userEmail) {
        // Stop proxying in this tab, but don't delete the key - another tab may own it.
        stopProxyingLocally();
        return;
      }
      var isStillGranted = proxyAccounts.some(function (account) {
        return account.id === stored.proxiedAccountId;
      });
      if (!isStillGranted) return;
      confirmProxyTarget(stored.proxiedAccountId);
      if (currentId !== stored.proxiedAccountId) window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return function () {
      return window.removeEventListener("storage", onStorage);
    };
  }, [userEmail, proxyableAccountIdsKey]);
};
export default useProxyAccountSession;